import { createApiRef } from '@backstage/core-plugin-api';
import type {
  ProcessAsIsApi,
  ProcessToBeApi,
} from './OrquestraApi.process.types';
import type { ImagineApi } from './OrquestraApi.imagine.types';
import type {
  CancelarParcelaInput,
  ConfirmDeliveryInput,
  ContractCount,
  CreateDeliverableInput,
  CreateEventInput,
  CreateParcelaInput,
  CreatePhaseInput,
  CreateProjectInput,
  CreateSprintInput,
  Deliverable,
  FinancialAuditLog,
  FinancialCharts,
  FinancialDashboard,
  ManagementDashboard,
  FinancialEvent,
  FinancialKpis,
  FinancialPlan,
  GeneratePlanInput,
  LancamentoInput,
  PaginatedResult,
  Parcela,
  Phase,
  PlanningDateRange,
  PlanningDateValidation,
  PlanningEvent,
  PlanningView,
  Project,
  AttachmentEntityType,
  EntityAttachment,
  ProjectAttachment,
  ProjectEditLog,
  ProjectHubSummary,
  ProjectListFilters,
  ProjectStatusAction,
  ProjectStatusLog,
  ProjectSummary,
  ImportModule,
  ImportResult,
  ReportExportRequest,
  Sprint,
  UpdateDeliverableInput,
  UpdateEventInput,
  UpdateParcelaInput,
  UpdatePhaseInput,
  UpdateProjectInput,
  UpdateSprintInput,
} from '@internal/orquestra-contract';

/** Opções de paginação dos audit-logs financeiros. */
export interface AuditLogsPageParams {
  limit?: number;
  offset?: number;
  actionTypes?: string[];
}

/** Página de audit-logs financeiros (offset-based). */
export interface AuditLogsPage {
  items: FinancialAuditLog[];
  total: number;
  hasMore: boolean;
}

/** Resposta do endpoint de prova de vida do backend. */
export interface OrquestraHealth {
  status: string;
}

/** Resposta do endpoint de identidade do usuário autenticado. */
export interface OrquestraWhoami {
  userEntityRef: string;
}

/**
 * Progresso emitido pelo `uploadAttachment` durante o envio multipart
 * (RF4.x). Reflete os bytes já transferidos e o total esperado.
 */
export interface AttachmentUploadProgress {
  loaded: number;
  total: number;
}

export type { ProjectAttachment };

/**
 * Contrato do client de API do plugin Orquestra. Cobre a fundação (health/
 * whoami), Projetos (Fase 1) e Planning (Fase 2).
 */
export interface OrquestraApi
  extends ProcessAsIsApi,
    ProcessToBeApi,
    ImagineApi {
  getHealth(): Promise<OrquestraHealth>;
  whoami(): Promise<OrquestraWhoami>;
  listProjects(filters?: ProjectListFilters): Promise<PaginatedResult<Project>>;
  getProject(id: number): Promise<Project>;
  createProject(input: CreateProjectInput): Promise<Project>;
  updateProject(id: number, input: UpdateProjectInput): Promise<Project>;
  deleteProject(
    id: number,
    justification?: string,
  ): Promise<{ action: string; message: string }>;
  getProjectSummary(): Promise<ProjectSummary>;
  getContractCount(
    contract: string,
    excludeId?: number,
  ): Promise<ContractCount>;
  changeProjectStatus(
    id: number,
    action: ProjectStatusAction,
    justification?: string,
  ): Promise<Project>;
  getStatusHistory(id: number): Promise<ProjectStatusLog[]>;
  getEditLog(id: number): Promise<ProjectEditLog[]>;
  getHubSummary(id: number): Promise<ProjectHubSummary>;
  // Planning (Fase 2)
  getPlanning(projectId: number): Promise<PlanningView>;
  createPhase(projectId: number, input: CreatePhaseInput): Promise<Phase>;
  updatePhase(
    projectId: number,
    phaseId: number,
    input: UpdatePhaseInput,
  ): Promise<Phase>;
  deletePhase(
    projectId: number,
    phaseId: number,
    deletionReason: string,
  ): Promise<void>;
  validatePhaseDates(
    projectId: number,
    phaseId: number,
    range: PlanningDateRange,
  ): Promise<PlanningDateValidation>;
  createSprint(
    projectId: number,
    phaseId: number,
    input: CreateSprintInput,
  ): Promise<Sprint>;
  updateSprint(
    projectId: number,
    phaseId: number,
    sprintId: number,
    input: UpdateSprintInput,
  ): Promise<Sprint>;
  deleteSprint(
    projectId: number,
    phaseId: number,
    sprintId: number,
    deletionReason: string,
  ): Promise<void>;
  confirmDeliverable(
    projectId: number,
    deliverableId: number,
    input: ConfirmDeliveryInput,
  ): Promise<Deliverable>;
  revertDeliverable(
    projectId: number,
    phaseId: number,
    sprintId: number,
    deliverableId: number,
  ): Promise<Deliverable>;
  toggleEvent(projectId: number, eventId: number): Promise<PlanningEvent>;
  // CRUD de Events e Deliverables por sprint (bug fix — Onda 1)
  listEvents(
    projectId: number,
    phaseId: number,
    sprintId: number,
  ): Promise<PlanningEvent[]>;
  createEvent(
    projectId: number,
    phaseId: number,
    sprintId: number,
    input: CreateEventInput,
  ): Promise<PlanningEvent>;
  updateEvent(
    eventId: number,
    input: UpdateEventInput,
  ): Promise<PlanningEvent | null>;
  deleteEvent(eventId: number): Promise<boolean>;
  listDeliverables(
    projectId: number,
    phaseId: number,
    sprintId: number,
  ): Promise<Deliverable[]>;
  createDeliverable(
    projectId: number,
    phaseId: number,
    sprintId: number,
    input: CreateDeliverableInput,
  ): Promise<Deliverable>;
  updateDeliverable(
    deliverableId: number,
    input: UpdateDeliverableInput,
  ): Promise<Deliverable | null>;
  deleteDeliverable(deliverableId: number): Promise<boolean>;
  // Anexos de projeto (RF4.x)
  listAttachments(projectId: number): Promise<ProjectAttachment[]>;
  uploadAttachment(
    projectId: number,
    file: File,
    onProgress?: (progress: AttachmentUploadProgress) => void,
  ): Promise<ProjectAttachment>;
  deleteAttachment(projectId: number, attachmentId: number): Promise<void>;
  getAttachmentDownloadUrl(
    projectId: number,
    attachmentId: number,
    disposition?: 'inline' | 'attachment',
  ): Promise<string>;
  // Anexos de entidade (Processo AS IS/TO BE, Solução Imagine)
  listEntityAttachments(
    projectId: number,
    entityType: AttachmentEntityType,
    entityId: number,
  ): Promise<EntityAttachment[]>;
  uploadEntityAttachment(
    projectId: number,
    entityType: AttachmentEntityType,
    entityId: number,
    file: File,
    onProgress?: (progress: AttachmentUploadProgress) => void,
  ): Promise<EntityAttachment>;
  deleteEntityAttachment(
    projectId: number,
    entityType: AttachmentEntityType,
    entityId: number,
    attachmentId: number,
  ): Promise<void>;
  getEntityAttachmentDownloadUrl(
    projectId: number,
    entityType: AttachmentEntityType,
    entityId: number,
    attachmentId: number,
    disposition?: 'inline' | 'attachment',
  ): Promise<string>;
  // Financeiro (Fase 3)
  listParcelas(projectId: number): Promise<Parcela[]>;
  getParcela(projectId: number, parcelaId: number): Promise<Parcela>;
  createParcela(projectId: number, input: CreateParcelaInput): Promise<Parcela>;
  updateParcela(
    projectId: number,
    parcelaId: number,
    input: UpdateParcelaInput & { reason?: string | null },
  ): Promise<Parcela>;
  deleteParcela(
    projectId: number,
    parcelaId: number,
    deletionReason?: string | null,
  ): Promise<void>;
  lancarParcela(
    projectId: number,
    parcelaId: number,
    input: LancamentoInput,
  ): Promise<Parcela>;
  cancelarParcela(
    projectId: number,
    parcelaId: number,
    input: CancelarParcelaInput,
  ): Promise<Parcela>;
  getFinancialKpis(projectId: number): Promise<FinancialKpis>;
  getPlan(projectId: number): Promise<FinancialPlan>;
  generatePlan(
    projectId: number,
    input: GeneratePlanInput,
  ): Promise<FinancialPlan>;
  updatePlan(
    projectId: number,
    input: GeneratePlanInput,
  ): Promise<FinancialPlan>;
  getFinancialEvents(projectId: number): Promise<FinancialEvent[]>;
  getFinancialCharts(projectId: number): Promise<FinancialCharts>;
  getAuditLogs(projectId: number): Promise<FinancialAuditLog[]>;
  getAuditLogsPage(
    projectId: number,
    params?: AuditLogsPageParams,
  ): Promise<AuditLogsPage>;
  // Dashboards (fatia 3.1)
  getFinancialDashboard(): Promise<FinancialDashboard>;
  // Dashboard gerencial (fatia 4.4)
  getManagementDashboard(projectId?: number): Promise<ManagementDashboard>;
  // Report export (fatia 5.1)
  exportReport(
    projectId: number,
    request: ReportExportRequest,
  ): Promise<{ blob: Blob; fileName: string }>;
  // Imports (fatia 5.3)
  downloadImportTemplate(
    projectId: number,
    module: ImportModule,
  ): Promise<{ blob: Blob; fileName: string }>;
  importSpreadsheet(
    projectId: number,
    module: ImportModule,
    file: File,
  ): Promise<ImportResult>;
}

export const orquestraApiRef = createApiRef<OrquestraApi>({
  id: 'plugin.orquestra.api',
});
